// Simulated customer-service agent for the Live Chat demo.
// Replies are chosen by keyword matching; no backend involved.

export const AGENT_NAME = 'Sarah';
export const AGENT_TITLE = 'Siegfried Customer Service';

export const SALES_REP_ACTION = 'Find my sales representative';

export const DEFAULT_SUGGESTIONS = ['Our sites', 'Order status', 'Request a quote', 'Talk to a human'];

const FALLBACK_REPLIES = [
    "I'm not sure I fully understood. Could you tell me a bit more? I can help with our sites, orders, projects, quotes or quality topics.",
    'Good question! Let me point you in the right direction: are you asking about our manufacturing sites, an ongoing order, or a new project?',
    "I'd be happy to help with that. To route your request correctly, could you specify whether it concerns a drug substance or a drug product?",
];

let fallbackIndex = 0;

const includesAny = (text, keywords) => keywords.some((k) => text.includes(k));

const RULES = [
    {
        keywords: ['human', 'agent', 'person', 'someone', 'call me', 'call back'],
        reply: () => ({
            text: 'Of course! I have notified a customer service specialist, who will join this conversation within a few minutes. Meanwhile, you can also reach your dedicated sales representative directly.',
            suggestions: [SALES_REP_ACTION, 'Opening hours'],
        }),
    },
    {
        keywords: ['thank', 'thx', 'bye', 'goodbye', 'see you'],
        reply: () => ({
            text: "You're very welcome! Is there anything else I can do for you today?",
            suggestions: ['No, that is all', ...DEFAULT_SUGGESTIONS.slice(0, 2)],
        }),
    },
    {
        keywords: ['no, that is all', 'that is all', "that's all", 'nothing else'],
        reply: () => ({
            text: 'Thank you for contacting Siegfried. Have a great day! 👋',
            suggestions: [],
        }),
    },
    {
        keywords: ['hello', 'hi ', 'hi!', 'hey', 'good morning', 'good afternoon', 'good evening'],
        exact: ['hi'],
        reply: ({ isAuthenticated, userName }) => ({
            text: isAuthenticated
                ? `Hello ${userName}! Nice to see you again. What can I do for you today?`
                : 'Hello and welcome to Siegfried! What can I do for you today?',
            suggestions: DEFAULT_SUGGESTIONS,
        }),
    },
    {
        keywords: ['order', 'delivery', 'shipment', 'shipping', 'tracking', 'status'],
        reply: ({ isAuthenticated, userName }) =>
            isAuthenticated
                ? {
                    text: `Let me check that for you, ${userName}… Your latest order SFZ-2026-0418 has been released by Quality Assurance and is scheduled for shipment from Zofingen on Friday. You can find all details under "My Orders".`,
                    suggestions: ['Delivery documents', 'Change delivery date', 'Talk to a human'],
                }
                : {
                    text: 'To look up your orders I need to know who you are. Please log in via the "Connect" button first, then ask me again.',
                    suggestions: ['Our sites', 'Request a quote'],
                },
    },
    {
        keywords: ['document', 'coa', 'certificate', 'invoice'],
        reply: () => ({
            text: 'The Certificate of Analysis and shipping documents are generated at release. I have requested a copy to be sent to the email address on your account.',
            suggestions: ['Order status', 'Talk to a human'],
        }),
    },
    {
        keywords: ['change delivery', 'reschedule', 'postpone'],
        reply: () => ({
            text: 'I have forwarded your rescheduling request to our supply chain team. They will confirm a new delivery date within one business day.',
            suggestions: ['Order status', 'Talk to a human'],
        }),
    },
    {
        keywords: ['project', 'development', 'tech transfer', 'scale-up', 'scale up'],
        reply: ({ isAuthenticated }) => ({
            text: isAuthenticated
                ? 'Your projects are visible under "My Projects", with their current milestones. Your project manager will share the next progress report at the end of the week.'
                : 'Siegfried supports projects from process development and scale-up through to commercial manufacturing. Log in to follow the progress of your own projects.',
            suggestions: ['Request a quote', 'Our sites'],
        }),
    },
    {
        keywords: ['quote', 'price', 'pricing', 'cost', 'offer', 'sales', 'buy', 'proposal', 'rfp'],
        reply: ({ isAuthenticated }) => ({
            text: isAuthenticated
                ? 'For a quote, your dedicated sales representative is the best contact. Tap below to find the right person for your product area and country.'
                : 'For a quote, our sales representatives will get back to you quickly. Log in to see the representative assigned to your area and country.',
            suggestions: isAuthenticated ? [SALES_REP_ACTION, 'Talk to a human'] : ['Talk to a human', 'Our sites'],
        }),
    },
    {
        keywords: ['substance', 'api', 'active pharmaceutical'],
        reply: () => ({
            text: 'Our Drug Substance network covers API development and manufacturing, from small-scale clinical batches to large commercial volumes, including highly potent and controlled substances. Sites include Zofingen, Evionnaz, Minden, St. Vulbas, Pennsville, Grafton and Nantong.',
            suggestions: ['Drug product sites', 'Request a quote'],
        }),
    },
    {
        keywords: ['product site', 'drug product', 'finished dosage', 'sterile', 'injectable', 'solids', 'inhalation', 'ophthalmic'],
        reply: () => ({
            text: 'Our Drug Product sites manufacture finished dosage forms: solids, semi-solids, inhalation, ophthalmics and sterile injectables (ampoules, vials and prefilled syringes). Sites include Hameln, El Masnou, Hal Far, Irvine and Barberà del Vallès.',
            suggestions: ['Drug substance sites', 'Request a quote'],
        }),
    },
    {
        keywords: ['site', 'location', 'plant', 'facility', 'factory', 'where', 'country', 'countries'],
        reply: () => ({
            text: 'Siegfried operates a global network of 16 sites across four continents, headquartered in Zofingen, Switzerland. Would you like to know more about our drug substance or drug product sites?',
            suggestions: ['Drug substance sites', 'Drug product sites'],
        }),
    },
    {
        keywords: ['quality', 'gmp', 'fda', 'audit', 'inspection', 'regulatory', 'compliance', 'swissmedic', 'ema'],
        reply: () => ({
            text: 'All Siegfried sites operate under cGMP and are regularly inspected by authorities such as FDA, EMA and Swissmedic. Customer audits can be scheduled through your sales representative or project manager.',
            suggestions: ['Request a quote', 'Talk to a human'],
        }),
    },
    {
        keywords: ['hour', 'open', 'phone', 'contact', 'email', 'reach'],
        reply: () => ({
            text: 'Our customer service team is available Monday to Friday, 8:00 to 18:00 CET. Outside these hours, leave a message here and we will get back to you on the next business day.',
            suggestions: ['Talk to a human', 'Request a quote'],
        }),
    },
    {
        keywords: ['about', 'history', 'founded', 'who are you', 'company', 'siegfried', 'employees'],
        reply: () => ({
            text: 'Siegfried was founded in 1873 in Zofingen, Switzerland. Today we are a leading global CDMO with more than 3,800 employees, serving over 500 customers worldwide.',
            suggestions: ['Our sites', 'Request a quote'],
        }),
    },
];

export const getBotReply = (input, context) => {
    const text = input.toLowerCase().trim();
    const rule = RULES.find((r) => (r.exact && r.exact.includes(text)) || includesAny(text, r.keywords));
    if (rule) return rule.reply(context);
    const reply = FALLBACK_REPLIES[fallbackIndex % FALLBACK_REPLIES.length];
    fallbackIndex += 1;
    return { text: reply, suggestions: DEFAULT_SUGGESTIONS };
};

// Simulated "typing" time: longer answers take a bit longer to type.
export const getTypingDelay = (reply) => Math.min(2500, 900 + reply.length * 8);
